import { useId, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import { convert, envelopeRange, formatUnit, unitLabel, type EnvelopePoint, type UnitSystem } from '@jupi/shared'
import { useUnitSystem } from '../hooks/useUnitSystem'

export interface EnvelopeMarker {
  label: string
  arm: number
  kg: number
  color: string
}

// The CG envelope as a chart: arm across, mass up (metric, or the flight
// manual's inches and pounds when the viewer has chosen imperial), with the
// loading's take-off and landing points marked. Plain SVG in a viewBox, so
// it scales to whatever width the phone gives it and needs no chart
// library; webplan drew the same thing with Recharts.
export function EnvelopeChart({
  envelope,
  markers = [],
  bothUnits = false,
  action,
}: {
  envelope: EnvelopePoint[]
  markers?: EnvelopeMarker[]
  /** Label both metric and imperial, instead of the viewer's unit system alone. */
  bothUnits?: boolean
  /** Shown to the right of the key. */
  action?: ReactNode
}) {
  const { t } = useTranslation()
  const theme = useTheme()
  const system = useUnitSystem()
  const titleId = useId()
  const range = envelopeRange([...envelope, ...markers])
  if (!range || envelope.length < 3) return null

  const W = 320
  const H = 220
  const systems: UnitSystem[] = bothUnits ? ['metric', 'imperial'] : [system]
  const pad = { left: bothUnits ? 58 : 44, right: 12, top: 12, bottom: bothUnits ? 42 : 30 }
  // A margin around the data, so a point on the envelope's edge is not on
  // the chart's.
  const armSpan = range.maxArm - range.minArm || 0.1
  const kgSpan = range.maxKg - range.minKg || 100
  const minArm = range.minArm - armSpan * 0.08
  const maxArm = range.maxArm + armSpan * 0.08
  const minKg = range.minKg - kgSpan * 0.08
  const maxKg = range.maxKg + kgSpan * 0.08
  const x = (arm: number) => pad.left + ((arm - minArm) / (maxArm - minArm)) * (W - pad.left - pad.right)
  const y = (kg: number) => H - pad.bottom - ((kg - minKg) / (maxKg - minKg)) * (H - pad.top - pad.bottom)

  const polygon = envelope.map((p) => `${x(p.arm)},${y(p.kg)}`).join(' ')
  const armTicks = [range.minArm, (range.minArm + range.maxArm) / 2, range.maxArm]
  const kgTicks = [range.minKg, (range.minKg + range.maxKg) / 2, range.maxKg]
  const axis = theme.palette.text.secondary

  return (
    <Box>
      <Box
        component="svg"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-labelledby={titleId}
        sx={{ width: '100%', height: 'auto', display: 'block', maxWidth: 560 }}
      >
        <title id={titleId}>{t('performance.envelope')}</title>
        <line x1={pad.left} y1={H - pad.bottom} x2={W - pad.right} y2={H - pad.bottom} stroke={axis} strokeWidth={0.5} />
        <line x1={pad.left} y1={pad.top} x2={pad.left} y2={H - pad.bottom} stroke={axis} strokeWidth={0.5} />
        {armTicks.map((arm) => (
          <text key={`a${arm}`} x={x(arm)} y={H - pad.bottom + 14} fontSize={9} textAnchor="middle" fill={axis}>
            {systems.map((sys, i) => (
              <tspan key={sys} x={x(arm)} dy={i === 0 ? 0 : 11}>
                {formatUnit('arm', arm, sys, bothUnits && sys === 'imperial' ? 0 : undefined)} {bothUnits ? unitLabel('arm', sys) : ''}
              </tspan>
            ))}
          </text>
        ))}
        {kgTicks.map((kg) => (
          <text key={`k${kg}`} x={pad.left - 4} y={y(kg) + (bothUnits ? -2 : 3)} fontSize={9} textAnchor="end" fill={axis}>
            {systems.map((sys, i) => (
              <tspan key={sys} x={pad.left - 4} dy={i === 0 ? 0 : 10}>
                {Math.round(convert('mass', kg, sys))} {bothUnits ? unitLabel('mass', sys) : ''}
              </tspan>
            ))}
          </text>
        ))}
        {bothUnits ? null : (
          <>
            <text x={W - pad.right} y={H - 4} fontSize={9} textAnchor="end" fill={axis}>
              {unitLabel('arm', system)}
            </text>
            <text x={4} y={pad.top + 4} fontSize={9} fill={axis}>
              {unitLabel('mass', system)}
            </text>
          </>
        )}
        <polygon
          points={polygon}
          fill={theme.palette.success.main}
          fillOpacity={0.15}
          stroke={theme.palette.success.main}
          strokeWidth={1.5}
        />
        {markers.map((m) => (
          <circle key={m.label} cx={x(m.arm)} cy={y(m.kg)} r={5} fill={m.color} stroke={theme.palette.background.paper} strokeWidth={1.5} />
        ))}
      </Box>
      <Box sx={{ mt: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        {markers.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: bothUnits ? 0.5 : 2, flexDirection: bothUnits ? 'column' : 'row' }}>
          {markers.map((m) => (
            <Stack key={m.label} direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: m.color }} />
              <Typography variant="caption">
                {m.label}:{' '}
                {systems
                  .map((sys) => `${formatUnit('mass', m.kg, sys, 0)} ${unitLabel('mass', sys)} @ ${formatUnit('arm', m.arm, sys, bothUnits && sys === 'imperial' ? 0 : undefined)} ${unitLabel('arm', sys)}`)
                  .join(' = ')}
              </Typography>
            </Stack>
          ))}
          </Box>
        ) : (
          <span />
        )}
        {action}
      </Box>
    </Box>
  )
}
